import { Check, Clock3, MapPin } from "lucide-react";
import { fileUrl } from "@/lib/api";

function segmentAfter(station, nextStation, segments) {
  if (!station || !nextStation) return null;
  const matches = (segments || []).filter(
    (segment) => segment.from_station_id === station.id && segment.to_station_id === nextStation.id
  );
  if (!matches.length) return null;
  if (matches.length === 1) return matches[0];
  return {
    duration_text: matches.map((segment) => segment.duration_text).filter(Boolean).join(" / "),
    distance_text: matches.map((segment) => segment.distance_text).filter(Boolean).join(" / "),
  };
}

export default function RouteOverview({ stations, segments = [], completedStationIds = [] }) {
  const completed = new Set(completedStationIds);

  return (
    <section className="route-overview" aria-labelledby="route-overview-title" data-testid="route-overview">
      <div className="route-overview__intro">
        <p className="eyebrow !text-[#b9c9be]">Die Route</p>
        <h2 id="route-overview-title" className="mt-1 text-3xl text-white">Sechs Orte. Eine zusammenhängende Spur.</h2>
        <p className="mt-2 max-w-lg text-sm leading-relaxed text-[#d7e0d9]">
          Vom Kunstpark durch den Ort und wieder zurück. Jede Station öffnet erst ihre Geschichte, danach folgt der Weg zum nächsten Punkt.
        </p>
      </div>

      <ol className="route-overview__list">
        {stations.map((station, index) => {
          const next = stations[index + 1];
          const connector = segmentAfter(station, next, segments);
          const isDone = completed.has(station.id);
          return (
            <li key={station.id} id={`station-${station.id}`} className="route-overview__stop">
              <span className={`route-overview__marker ${isDone ? "is-done" : ""}`} aria-label={isDone ? "Station abgeschlossen" : `Station ${index + 1}`}>
                {isDone ? <Check className="h-4 w-4" /> : String(index + 1).padStart(2, "0")}
              </span>

              <article className="route-overview__card">
                {station.hero_image ? (
                  <img src={fileUrl(station.hero_image)} alt="" className="route-overview__image" />
                ) : (
                  <div className="route-overview__image route-overview__image--empty" aria-hidden="true" />
                )}
                <div className="min-w-0 flex-1 p-4">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--brand-accent)]">
                    Spur {String(index + 1).padStart(2, "0")}
                  </p>
                  <h3 className="mt-1 text-2xl leading-none text-[var(--ink)]">{station.title}</h3>
                  {station.subtitle ? <p className="mt-1 text-xs font-semibold text-[var(--ink-soft)]">{station.subtitle}</p> : null}
                  {station.location_description ? (
                    <p className="mt-3 flex items-start gap-1.5 text-xs leading-relaxed text-[var(--ink-soft)]">
                      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {station.location_description}
                    </p>
                  ) : null}
                </div>
              </article>

              {next && (connector?.duration_text || connector?.distance_text) ? (
                <div className="route-overview__connector" aria-label={`Weg zu ${next.title}`}>
                  {connector.duration_text ? <span><Clock3 className="h-3.5 w-3.5" /> {connector.duration_text}</span> : null}
                  {connector.distance_text ? <span>{connector.distance_text}</span> : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
